import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import ErrorPage from 'app/components/ErrorPage';
import { TOP_LEVEL_CATEGORY_ID } from 'app/constants/AdminBrowseConstants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { hasAdminPermissions } from 'app/helpers/browse/admin';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectLocalization } from 'app/selectors/LocalizationSelector';
import AdminBrowseCategoriesPage from './AdminBrowseCategoriesPage';
import CategoryConfigEditor from './CategoryConfigEditor';
import CategoryConfigsList from './CategoryConfigsList';

const AdminBrowseCategoriesPageContainer = ({ basePageRoute }) => {
  const history = useHistory();
  const params = useParams();
  const currentLibrary = useSelector(selectCurrentLibrary);
  const currentUser = useSelector(selectCurrentUser);
  const localization = useSelector(selectLocalization);

  const supportedLanguageKeys = localization.availableLanguages;

  const { categoryId: categoryIdParam, languageKey: languageKeyParam = null } = params;

  const [state, setState] = useState({
    error: '',
    languageKey: languageKeyParam,
    topLevelCategoryId: '/'.concat(categoryIdParam)
  });

  useEffect(() => {
    const topLevelCategoryId = '/'.concat(categoryIdParam);
    const isValidTopLevelCategoryId = Object.values(TOP_LEVEL_CATEGORY_ID).includes(topLevelCategoryId);

    setState(prevState => {
      return {
        ...prevState,
        error: isValidTopLevelCategoryId ? '' : `Invalid category param: ${categoryIdParam}`,
        languageKey: null,
        topLevelCategoryId: isValidTopLevelCategoryId ? topLevelCategoryId : ''
      };
    });
  }, [categoryIdParam]);

  useEffect(() => {
    const isValidLanguageKey = !languageKeyParam || supportedLanguageKeys.includes(languageKeyParam);

    setState(prevState => {
      return {
        ...prevState,
        error: isValidLanguageKey ? '' : `Invalid language key param: ${languageKeyParam}`,
        languageKey: isValidLanguageKey ? languageKeyParam : null
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageKeyParam]);

  const subdomain = currentLibrary.get('domain');
  const canViewPage = hasAdminPermissions(currentUser);
  const currentUsername = currentUser?.get('name');

  const onLanguageKeySelected = useCallback(
    selectedLanguageKey => {
      const { topLevelCategoryId } = state;
      history.push(`${basePageRoute}${topLevelCategoryId}/${selectedLanguageKey}`);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basePageRoute, state.topLevelCategoryId]
  );

  const onTabSelected = useCallback(
    selectedTab => {
      const { id: topLevelCategoryId } = selectedTab;
      history.push(`${basePageRoute}${topLevelCategoryId}`);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basePageRoute]
  );

  const renderPanelBody = useCallback(() => {
    const { error, languageKey, topLevelCategoryId } = state;

    if (error) {
      return <p style={{ color: 'red' }}>{error}</p>;
    }
    if (languageKey && topLevelCategoryId) {
      return (
        <CategoryConfigEditor
          backToUrl={`${basePageRoute}${topLevelCategoryId}`}
          currentUsername={currentUsername}
          languageKey={languageKey}
          subdomain={subdomain}
          topLevelCategoryId={topLevelCategoryId}
        />
      );
    }
    if (topLevelCategoryId) {
      return (
        <CategoryConfigsList
          languageKeys={supportedLanguageKeys}
          onLanguageKeySelected={onLanguageKeySelected}
          subdomain={subdomain}
          topLevelCategoryId={topLevelCategoryId}
        />
      );
    }

    return <p style={{ color: 'red' }}>Something went wrong. 😞</p>;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const tabOptions = useMemo(() => {
    // TODO: localize labels?
    return [
      {
        id: TOP_LEVEL_CATEGORY_ID.COLLECTION,
        label: 'Collection',
        renderPanelBody
      },
      {
        id: TOP_LEVEL_CATEGORY_ID.FORMAT,
        label: 'Format',
        renderPanelBody
      },
      {
        id: TOP_LEVEL_CATEGORY_ID.GENRE,
        label: 'Genre',
        renderPanelBody
      }
    ];
  }, [renderPanelBody]);

  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  return (
    <AdminBrowseCategoriesPage
      onTabSelected={onTabSelected}
      tabOptions={tabOptions}
      topLevelCategoryId={state.topLevelCategoryId}
    />
  );
};

AdminBrowseCategoriesPageContainer.propTypes = {
  basePageRoute: PropTypes.string.isRequired
};

export default AdminBrowseCategoriesPageContainer;
